import { Check, Link as LinkIcon, AlertTriangle, type LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Skeleton } from '@/components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';

/**
 * Today's tally strip (86d3rrfbf).
 *
 * THREE tiles now, from the prototype's five. "Waiting NS" went first (decision 4): with the
 * multi-day retry ladder gone, waiting is a seconds-to-minutes transient that would read 0 almost
 * always, and a permanently-zero tile teaches people to stop reading the whole row.
 *
 * "Duplicates" went with 86d3yek7d, on the owner's call. A duplicate upload is recorded NOWHERE
 * durable — intake returns early with no row, no counter, no log line and no stream event — so the
 * only "duplicates today" this tile could show was whatever this browser tab happened to have counted
 * since it loaded, and that vanished on reload. A number that resets when you press F5 is worse than
 * no number. Duplicate scans stay visible where they are actually useful: as "Already captured" rows
 * in the live feed, in front of the person who just fed the paper.
 *
 * ⚠️ The two day tiles and the Review tile measure DIFFERENT THINGS on purpose, and the tooltips have
 * to say so: Captured/Linked describe the cohort of files uploaded today; Review is everything open
 * right now, whenever it was scanned.
 */

type Tone = 'success' | 'warning' | 'muted';

interface Tile {
  label: string;
  /** null = not available (still loading, or the fetch failed) — the tile shows a dash, never a 0. */
  value: number | null;
  icon: LucideIcon;
  tone: Tone;
  tip: string;
}

const TONE_CLASSES: Record<Tone, string> = {
  success: 'bg-success/12 text-success',
  warning: 'bg-warning/12 text-warning',
  muted: 'bg-muted text-muted-foreground',
};

interface TallyStripProps {
  /** Of the files uploaded today, how many are attached. Null while unknown. */
  captured: number | null;
  /** Of the files uploaded today, how many belong to a linked document. Null while unknown. */
  linked: number | null;
  /** Open review items right now — NOT day-scoped, and the tooltip says so. */
  review: number;
  /** The first load of today's numbers is in flight; the two day tiles show skeletons. */
  loading: boolean;
}

export function TallyStrip({ captured, linked, review, loading }: TallyStripProps) {
  const tiles: Tile[] = [
    {
      label: 'Captured',
      value: captured,
      icon: Check,
      tone: 'success',
      // 86d3yek7d — was "Files whose barcode was read and filed today", which promised an event
      // count. It is a state: of today's uploads, how many are filed RIGHT NOW.
      tip: 'Of the files uploaded today, how many are now filed against a document. This is where today’s scans stand at the moment — not a running count of what happened.',
    },
    {
      label: 'Linked',
      value: linked,
      icon: LinkIcon,
      tone: 'success',
      tip: 'Of the files uploaded today, how many are attached to their NetSuite transaction right now. A scan uploaded today but linked tomorrow shows up in tomorrow’s count.',
    },
    {
      label: 'Review',
      value: review,
      icon: AlertTriangle,
      tone: 'warning',
      tip: 'Everything waiting for a person right now — from any day, not only today’s scans. These are the exceptions the system could not decide safely.',
    },
  ];

  return (
    <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
      {tiles.map((tile) => {
        const Icon = tile.icon;
        return (
          <Tooltip key={tile.label}>
            <TooltipTrigger asChild>
              {/* Geometry is the prototype's, to the pixel: 34px tile / r9 / 17px glyph, 11px gap,
                  20px number. Every tile is tinted — an untinted glyph beside two tinted ones
                  reads as a different kind of thing. */}
              <div className="flex cursor-help items-center gap-[11px] rounded-[11px] border border-border bg-card px-[14px] py-3">
                <span
                  className={cn(
                    'flex h-[34px] w-[34px] flex-shrink-0 items-center justify-center rounded-[9px]',
                    TONE_CLASSES[tile.tone]
                  )}
                >
                  <Icon className="h-[17px] w-[17px]" aria-hidden />
                </span>
                <div className="min-w-0">
                  {/* A skeleton, not a 0. Printing 0 while the count is still in flight asserts
                      something false about the day — and this is the number being checked. */}
                  {loading && tile.value === null ? (
                    <Skeleton className="h-5 w-8" />
                  ) : (
                    <div className="text-[20px] font-bold leading-none tabular-nums text-foreground">
                      {tile.value ?? '—'}
                    </div>
                  )}
                  <div className="mt-0.5 text-[11.5px] text-muted-foreground">{tile.label}</div>
                </div>
              </div>
            </TooltipTrigger>
            <TooltipContent className="max-w-xs">
              {tile.tip}
              {tile.value === null && !loading && (
                <span className="mt-1 block text-muted-foreground">
                  Today’s numbers could not be loaded — they will refresh with the next scan.
                </span>
              )}
            </TooltipContent>
          </Tooltip>
        );
      })}
    </div>
  );
}
